import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { ActivitySession } from '../types';

interface SessionsSectionProps {
  sessions: ActivitySession[];
  onEdit?: () => void;
  readOnly?: boolean;
}

const AR_DAYS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AR_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

function formatSessionDate(dateStr: string, isRTL: boolean): { dayName: string; dateLine: string } {
  const d = new Date(dateStr + 'T00:00:00');
  const dayIdx = d.getDay();
  const dayName = isRTL ? AR_DAYS[dayIdx] : EN_DAYS[dayIdx];
  const day = d.getDate();
  const month = isRTL ? AR_MONTHS[d.getMonth()] : d.toLocaleString('en', { month: 'long' });
  const year = d.getFullYear();
  const dateLine = isRTL ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  return { dayName, dateLine };
}

function formatTime(time: string): string {
  return time.substring(0, 5);
}

function SessionCard({ session, isRTL }: { session: ActivitySession; isRTL: boolean }) {
  const { dayName, dateLine } = formatSessionDate(session.date, isRTL);
  const timeRange = `${formatTime(session.startTime)} - ${formatTime(session.endTime)}`;

  return (
    <View style={cardStyles.container}>
      <Text style={cardStyles.text}>{dayName}</Text>
      <Text style={cardStyles.text}>{dateLine}</Text>
      <Text style={cardStyles.text}>{timeRange}</Text>
    </View>
  );
}

export default function SessionsSection({ sessions, onEdit, readOnly }: SessionsSectionProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        {onEdit && !readOnly && (
          <TouchableOpacity
            style={[styles.editBtn, { borderColor: colors.cardBorder, backgroundColor: 'rgba(51,65,85,0.05)' }]}
            onPress={onEdit}
            activeOpacity={0.7}
          >
            <Ionicons name="create-outline" size={15} color={colors.textPrimary} />
          </TouchableOpacity>
        )}
        <Text style={[styles.sectionTitle, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {isRTL ? 'الجلسات' : 'Sessions'}
        </Text>
      </View>

      {/* Session cards in muted container */}
      <View style={styles.cardsWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[
            styles.cardsScroll,
            {
              flexDirection: isRTL ? 'row-reverse' : 'row',
              flexGrow: 1,
            },
          ]}
        >
          {sessions.map((session) => (
            <SessionCard key={session.id} session={session} isRTL={isRTL} />
          ))}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    flex: 1,
  },
  editBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardsWrapper: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
  },
  cardsScroll: {
    gap: 20,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    width: 160,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#A4EFE7',
    padding: 12,
    gap: 10,
  },
  text: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
});
